import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle, LogIn } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { authService } from "@/features/auth/services/authService";
import { handleApiError } from "@/lib/utils/errorHandler";

type Status = "idle" | "verifying" | "success" | "error";

export function EmailVerificationPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState<Status>(token ? "verifying" : "error");
  const [error, setError] = useState<string | null>(
    token ? null : "Invalid or missing verification link."
  );

  useEffect(() => {
    if (!token) return;

    const queryParams = Object.fromEntries(searchParams.entries());
    let cancelled = false;

    const verify = async () => {
      setStatus("verifying");
      setError(null);
      try {
        await authService.verifyEmail(queryParams);
        if (!cancelled) {
          setStatus("success");
        }
      } catch (err) {
        if (!cancelled) {
          const apiError = handleApiError(err);
          setError(apiError.message);
          setStatus("error");
        }
      }
    };

    verify();
    return () => {
      cancelled = true;
    };
  }, [token, searchParams]);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-md space-y-8">
          <Card className="shadow-medium">
            <CardHeader className="text-center pb-4">
              {status === "verifying" && (
                <div className="mx-auto mb-4 rounded-full bg-primary/10 p-4 w-fit">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              )}
              {status === "success" && (
                <div className="mx-auto mb-4 rounded-full bg-success/10 p-4 w-fit">
                  <CheckCircle2 className="h-8 w-8 text-success" />
                </div>
              )}
              {status === "error" && (
                <div className="mx-auto mb-4 rounded-full bg-destructive/10 p-4 w-fit">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                </div>
              )}
              <CardTitle className="text-xl">Email Verification</CardTitle>
              <CardDescription>
                {status === "verifying" && "Verifying your email address..."}
                {status === "success" && "Your email has been verified successfully."}
                {status === "error" && "Verification could not be completed."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {status === "verifying" && (
                <div className="flex flex-col items-center justify-center py-4">
                  <p className="text-sm text-muted-foreground">
                    Please wait while we verify your email...
                  </p>
                </div>
              )}

              {status === "success" && (
                <>
                  <Alert variant="success">
                    <CheckCircle2 className="h-4 w-4" />
                    <AlertTitle>Success</AlertTitle>
                    <AlertDescription>
                      Your email has been verified successfully! You can now sign in to your account.
                    </AlertDescription>
                  </Alert>
                  <Link to="/login">
                    <Button className="w-full h-11 gap-2">
                      <LogIn className="h-4 w-4" />
                      Sign in to your account
                    </Button>
                  </Link>
                </>
              )}

              {status === "error" && (
                <>
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Verification Failed</AlertTitle>
                    <AlertDescription>
                      {error ?? "Something went wrong during verification."}
                    </AlertDescription>
                  </Alert>
                  <div className="flex flex-col gap-2">
                    <Link to="/login">
                      <Button variant="outline" className="w-full">
                        Back to Sign In
                      </Button>
                    </Link>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <footer className="py-6 text-center">
        <p className="text-sm text-muted-foreground">
          © {new Date().getFullYear()} My Professional Tax. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
